<script setup lang="ts">
import { computed, ref } from "vue";
import UiTextInput from "@/components/ui/TextInput.vue";

export type DropdownItem = {
  label: string;
  value: string;
};

export type StatusMessage = {
  icon?: string;
  type: "error" | "warning" | "success" | "info";
  message: string;
};

const props = defineProps({
  modelValue: {
    type: Object as () => DropdownItem | null,
    default: null,
  },
  items: {
    type: Array as () => DropdownItem[],
    default: () => [],
  },
  placeholder: {
    type: String,
    default: "Vyberte možnost",
  },
  noDataText: {
    type: String,
    default: "Nic nebylo nalezeno.",
  },
  label: {
    type: String,
    default: "",
  },
  required: {
    type: Boolean,
    default: false,
  },
  id: {
    type: String,
    default: "",
  },
  maxHeight: {
    type: String,
  },
  statusMessage: {
    type: Object as () => StatusMessage | undefined,
  },
});
const emit = defineEmits(["update:modelValue"]);
const searchQuery = ref<string>("");
const isOpen = ref<boolean>(false);
const filteredItems = computed<DropdownItem[]>(() => {
  if (!searchQuery.value) {
    return props.items;
  }

  return props.items.filter((item: DropdownItem) => {
    return item.label.toLowerCase().includes(searchQuery.value.toLowerCase());
  });
});

const toggleOpen = (): void => {
  searchQuery.value = "";

  isOpen.value = !isOpen.value;
};

const open = (): void => {
  isOpen.value = true;
};

const onSelect = (item: DropdownItem): void => {
  emit("update:modelValue", item);
  searchQuery.value = item.label;
  isOpen.value = false;
};
</script>

<template>
  <div class="ui-dropdown">
      <div class="input">
        <label :for="props.id" @click="open">{{ props.label }}</label>

        <div class="row">
          <UiTextInput
            :id="props.id"
            :placeholder="props.placeholder"
            v-model="searchQuery"
            :required="props.required"
            :status-message="props.statusMessage"
            @input="open"
          />
          <Icon name="material-symbols:keyboard-arrow-down-rounded" class="icon" :class="{ 'opened': isOpen }" @click="toggleOpen" />
        </div>
      </div>


    <div class="items" v-show="isOpen" :class="{ 'maxHeight': props.maxHeight }" :style="{ '--max-items-height': props.maxHeight + 'px' }">
      <div class="item noData" v-if="filteredItems.length === 0">
        {{ props.noDataText }}
      </div>

      <div class="item" :class="{ 'selected': props.modelValue && props.modelValue.value === item.value }" v-for="item in filteredItems" :key="item.value" @click="() => onSelect(item)">
        {{ item.label }}
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.ui-dropdown {
  display: flex;
  flex-direction: column;
  position: relative;
  gap: 10px;

  .input {
    display: flex;
    flex-direction: column;
    gap: 10px;

    label {
      color: rgb(var(--input-label-color));
      font-weight: 500;
      font-size: 15px;
      cursor: pointer;
    }

    .row {
      display: flex;
      flex-direction: row;
      align-items: center;
      justify-content: center;
      gap: 10px;
      flex: 1;

      .icon {
        padding: 15px;
        font-size: 21px;
        cursor: pointer;
        color: rgb(var(--text-light-color));
        transition: 0.2s;

        &.opened {
          transform: rotate(180deg);
        }

        &:hover {
          color: rgb(var(--mini-title-light-color));
        }
      }
    }
  }

  .items {
    position: absolute;
    top: 100%;
    margin-top: 10px;
    background: rgb(var(--btn-secondary-background));
    border-radius: var(--normal-border-radius);
    color: rgb(var(--input-text-color));
    font-size: 18px;
    transition: 0.2s;
    width: 100%;
    overflow-y: auto;

    &.maxHeight {
      max-height: var(--max-items-height);
    }

    &::-webkit-scrollbar {
      width: var(--scrollbar-small-width);
    }

    .item {
      padding: 10px 15px;
      border-bottom: 1px solid rgba(var(--input-outline-color), 0.3);
      transition: 0.2s;

      &:not(.noData):hover {
        cursor: pointer;
        background: rgba(var(--btn-secondary-hover-background), 1);
      }

      &:first-child {
        border-top-left-radius: var(--normal-border-radius);
        border-top-right-radius: var(--normal-border-radius);
      }

      &:last-child {
        border-bottom: none;
        border-bottom-left-radius: var(--normal-border-radius);
        border-bottom-right-radius: var(--normal-border-radius);
      }
    }
  }
}
</style>
